import type { AfterViewInit, OnChanges, OnInit } from '@angular/core';
import { Component, inject, Input } from '@angular/core';
import type {
  AbstractControl,
  FormArray,
  FormControl,
  FormGroup,
  UntypedFormControl,
  UntypedFormGroup,
  ValidatorFn,
} from '@angular/forms';
import { FormBuilder } from '@angular/forms';
import { createNumberMask } from 'text-mask-addons/dist/textMaskAddons';
import type { Bond } from '../../../../models/bond';
import { EnterBalancesValidationServiceService } from '../../../services/enter-balances-validation-service.service';

type BondForm = FormGroup<{
  id: FormControl<string>;
  cusip: FormControl<any>;
  rcrClass: FormControl<any>;
  notionalFlag: FormControl<any>;
  factor: FormControl<any>;
  bondOriginalFace: FormControl<any>;
  originalFaceToDealer: FormControl<any>;
  originalFaceToFNMA: FormControl<any>;
  remicClass: FormControl<any>;
  exchangeOriginalFace: FormControl<any>;
  circulateAmt: FormControl<any>;
  uncirculateAmt: FormControl<any>;
  principalType: FormControl<any>;
  interestType: FormControl<any>;
  interestRate: FormControl<any>;
  delayDays: FormControl<any>;
  finalPaymentDate: FormControl<any>;
  bondClassName: FormControl<any>;
  currentBalanceExchanged: FormControl<any>;
}>;

@Component({
  standalone: false,
  selector: 'invd-smbs-exchange-table',
  templateUrl: './smbs-exchange-table.component.html',
  styleUrls: ['./smbs-exchange-table.component.scss'],
})
export class SmbsExchangeTableComponent implements OnInit, AfterViewInit, OnChanges {
  @Input() smbsRecombinationExchanges: any;
  @Input() clearBtnClick: boolean;
  public bondHasError = false;

  public smbsRecombinationExchangesCopy: any;
  public mRecombinationRows: FormArray<BondForm>;
  public rcrClass = 'rcrClass';
  public remicClass = 'remicClass';

  public numberMask: any;

  private fb = inject(FormBuilder);
  private enterBalancesValidationService = inject(EnterBalancesValidationServiceService);

  ngOnInit() {
    this.smbsRecombinationExchangesCopy = { ...this.smbsRecombinationExchanges };
    this.buildSmbsBonds(this.smbsRecombinationExchanges);
    this.numberMask = createNumberMask({
      prefix: '',
      allowDecimal: true,
      decimalLimit: 2,
    });
  }

  ngAfterViewInit() {
    this.onInitTooltipError();
    this.enterBalancesValidationService.isCalendarValid = true;
    this.enterBalancesValidationService.isRecombinationTableValid = true;
  }

  private createRecombinationFormBuilder(): void {
    this.mRecombinationRows = this.fb.array<BondForm>([]);
  }

  private buildSmbsBonds(recombinationGroup: any) {
    this.createRecombinationFormBuilder();
    recombinationGroup.recombinationRows
      .map((smbsBond: any) => {
        const bondID = this.uniqueBondID();
        const recombinationRowsControl = this.mRecombinationRows;
        return recombinationRowsControl.push(this.createBond(smbsBond, bondID));
      })
      .map((_smbsBondControl) => {
        this.resetBondControlsToDisable();
        this.markBondAsPaidOff();
      });
  }

  private generateBondID(): string {
    // eslint-disable-next-line no-bitwise
    return (((2 + Math.random()) * 0x10000) | 0).toString(16).slice(1);
  }

  private uniqueBondID(): string {
    return (
      this.generateBondID() +
      this.generateBondID() +
      '-' +
      this.generateBondID() +
      '-4' +
      this.generateBondID().slice(0, 3) +
      '-' +
      this.generateBondID() +
      '-' +
      this.generateBondID() +
      this.generateBondID() +
      this.generateBondID()
    ).toLowerCase();
  }

  private createBond(bond: any, bondID: string): UntypedFormGroup {
    const mGroup = this.fb.group({
      id: bondID,
      cusip: [bond.cusip],
      rcrClass: [bond.rcrClass],
      notionalFlag: [bond.notionalFlag],
      factor: [bond.factor],
      bondOriginalFace: [bond.bondOrigFace],
      originalFaceToDealer: [bond.origFaceToDealer, this.balanceInputValidator(bond.exchOrigFace)],
      originalFaceToFNMA: [bond.origFaceToFNMA, this.balanceInputValidator(bond.exchOrigFace)],
      remicClass: [bond.remicClass],
      exchangeOriginalFace: [bond.exchOrigFace],
      circulateAmt: [bond.circulatedAmount],
      uncirculateAmt: [bond.uncirculatedAmount],
      principalType: [bond.principalType],
      interestType: [bond.interestType],
      interestRate: [bond.interestRate],
      delayDays: [bond.delayDays],
      finalPaymentDate: [bond.finalPaymentDate],
      bondClassName: [bond.bondClassName],
      currentBalanceExchanged: this.repopulateCurrentBalanceExchanged(bond) || 0,
    });

    return mGroup;
  }

  private repopulateCurrentBalanceExchanged(bond: any): number {
    const exchangeTypeValue: number = bond.origFaceToDealer || bond.origFaceToFNMA;

    if (isNaN(exchangeTypeValue)) {
      return 0;
    } else {
      const computedBondValue: number =
        Math.ceil(bond.factor * exchangeTypeValue * 100 - 0.5) / 100;
      return computedBondValue;
    }
  }

  private balanceInputValidator(originalBal: number): ValidatorFn {
    return (control: UntypedFormControl): { [s: string]: boolean } => {
      // let controlValue: number = parseInt(control.value);
      if (!control.value) {
        return null;
      }
      const planeNumber = String(control.value).split(',').join('');
      const controlValue = Number(planeNumber);
      if (controlValue > originalBal || controlValue < 0) {
        this.bondHasError = true;
        return { invalidBondExchange: true };
      } else {
        this.bondHasError = false;
        return { invalidBondExchange: false };
      }
    };
  }

  public captureBondExchangeEvent(
    event: KeyboardEvent | FocusEvent | Event,
    currentControl: UntypedFormControl,
    indexOfCurrentControl: number,
  ): void {
    if (this.bondHasError) {
      //#TODO show error future hook
    } else {
      // eslint-disable-next-line deprecation/deprecation
      if ('keyCode' in event && event.keyCode === 13) {
        currentControl.markAsPristine();
        if (currentControl.value <= 0) {
          this.resetFormGroupOnEmptyField(currentControl.parent);
        } else {
          this.calculateBondExchanges(indexOfCurrentControl);
        }
      } else if (event.type === 'blur' && currentControl.dirty) {
        currentControl.markAsPristine();
        if (currentControl.value <= 0) {
          this.resetFormGroupOnEmptyField(currentControl.parent);
        } else {
          this.calculateBondExchanges(indexOfCurrentControl);
        }
      }
    }
    const usrInputValue = Number(this.clearSeparators(currentControl.value.toString()));
    currentControl.setValue(Math.floor(usrInputValue));
  }

  private markBondAsPaidOff(): void {
    const recombinationRowsControl = this.mRecombinationRows;
    recombinationRowsControl.controls.filter((bondControl: BondForm) => {
      if (bondControl.value.factor === 0) {
        bondControl.get('currentBalanceExchanged').setValue(0);
        bondControl.get('originalFaceToFNMA').setValue(null);
        bondControl.get('originalFaceToDealer').setValue(null);
        bondControl.disable();
      }
    });
  }

  private calculateBondControls(currentBondControl: AbstractControl) {
    const bond: Bond = currentBondControl.value;

    currentBondControl
      .get('currentBalanceExchanged')
      .setValue(this.currentBalanceExchangedCalculation(bond));
  }

  private calculateBondExchanges(indexOfCurrentControl: number): void {
    // 1. find current control within the group
    // 2. calculate other controls of the same type
    //    2a. set the value of the controls
    //    2b. set the opposite control to disable
    // 3. calculate other controls of the opposite type
    //    3a. set the value of the controls
    //    3b. set the opposite control to disable

    const currentControl = this.lookupControlByIndex(indexOfCurrentControl);
    this.calculateBondControls(currentControl);
    this.disableOppositeCurrentBondControl(currentControl);
  }

  private lookupControlByIndex(index: number): AbstractControl {
    const recombinationRowsControl = this.mRecombinationRows;
    return recombinationRowsControl.at(index);
  }

  private resetFormGroupOnEmptyField(currentControl: AbstractControl): void {
    currentControl.get('originalFaceToDealer').enable();
    currentControl.get('originalFaceToFNMA').enable();

    currentControl.get('originalFaceToDealer').reset();
    currentControl.get('originalFaceToFNMA').reset();

    currentControl.get('currentBalanceExchanged').setValue(0);
  }

  private clearGroup(): void {
    this.mRecombinationRows.reset();
    this.buildSmbsBonds(this.smbsRecombinationExchangesCopy);
  }

  private currentBalanceExchangedCalculation(bond: Bond): number {
    const exchangeTypeValue: number = bond.originalFaceToDealer || bond.originalFaceToFNMA;
    const computedBondValue: number =
      Math.ceil(
        bond.factor * Number(this.clearSeparators(exchangeTypeValue.toString())) * 100 - 0.5,
      ) / 100;
    return computedBondValue;
  }

  private clearSeparators(inputNumber: string): string {
    return inputNumber.replace(/,/g, '');
  }

  private disableOppositeCurrentBondControl(currentControl: AbstractControl): void {
    const bond: Bond = currentControl.value;

    if (
      bond.originalFaceToFNMA !== null &&
      bond.originalFaceToFNMA !== undefined &&
      bond.originalFaceToFNMA !== 0
    ) {
      currentControl.get('originalFaceToDealer').setValue(0);
      currentControl.get('originalFaceToDealer').disable();
    }

    if (
      bond.originalFaceToDealer !== null &&
      bond.originalFaceToDealer !== undefined &&
      bond.originalFaceToDealer !== 0
    ) {
      currentControl.get('originalFaceToFNMA').setValue(0);
      currentControl.get('originalFaceToFNMA').disable();
    }
  }

  private resetBondControlsToDisable(): void {
    const recombinationRowsControl = this.mRecombinationRows;
    recombinationRowsControl.controls.filter((bondControl: BondForm) => {
      if (bondControl.value.originalFaceToFNMA === 0) {
        bondControl.get('originalFaceToFNMA').disable();
      }

      if (bondControl.value.originalFaceToDealer === 0) {
        bondControl.get('originalFaceToDealer').disable();
      }
    });
  }

  private onInitTooltipError(): void {}

  private onClearBtnClicked(): void {
    this.clearGroup();
  }

  ngOnChanges() {
    if (this.mRecombinationRows != null) {
      this.onClearBtnClicked();
    }
  }

  protected trackByIdentity(_index: number, obj: any) {
    return obj;
  }
}
